import { useRef, useState, useCallback, useMemo, memo } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { AdvancedOptionsInline } from "./AdvancedSearchForm";
import { Skeleton } from "primereact/skeleton";
import { useSearchFilters } from "./SearchFilterContext";
import { Toast } from "primereact/toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Skill {
  name: string;
  years: string;
}

export interface WorkAuthorization {
  authorization: string;
  country: string;
}

interface Candidate {
  id: number;
  textResumeID?: string;
  matchScore: number;
  name: string;
  location: string;
  currentTitle: string;
  company: string;
  resumeUpdated: string;
  experience: string;
  highestEducation: string;
  targetJobTitle: string;
  desiredJobType: string;
  desiredSalary: string;
  relocation: string;
  authorization: string;
  topSkills: Skill[];
}

interface CandidateResultsProps {
  results: Candidate[];
  totalResults: number;
  searchQuery: string;
  onNewSearch: () => void;
  onCandidateClick: (candidate: Candidate) => void;
  onUpdateFilters: (filters: any) => void;
  first: number;
  setFirst: (first: number) => void;
  onPage: (page: number) => void;
  isLoading?: boolean;
  useageData?: { quota: number; viewsRemaining: number };
  totalReturned: number;
}

const BOOLEAN_OPERATORS = new Set(["and", "or", "not"]);

const parseKeywords = (keywords: string): string[] => {
  const terms: string[] = [];

  const quotedRegex = /"([^"]+)"/g;
  let match;
  while ((match = quotedRegex.exec(keywords)) !== null) {
    const phrase = match[1].trim();
    if (phrase.length > 0) terms.push(phrase);
  }
  const withoutQuotes = keywords.replace(/"[^"]*"/g, "");
  const singleWords = withoutQuotes
    .split(/[\s,]+/)
    .map((k) => k.replace(/^[(]+|[')]+$/g, "").trim())
    .filter((k) => k.length > 0 && !BOOLEAN_OPERATORS.has(k.toLowerCase()));

  terms.push(...singleWords);
  return terms;
};

const highlightText = (text: string, keywords: string): React.ReactNode => {
  if (!keywords?.trim()) return text;
  const terms = parseKeywords(keywords);
  if (!terms.length) return text;
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const regex = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = text.split(regex);
  const testRegex = new RegExp(`^(${escaped.join("|")})$`, "i");

  return parts.map((part, i) =>
    testRegex.test(part) ? (
      <mark key={i} style={{ background: "#fff176", color: "#1a1d23", borderRadius: "3px", padding: "0 2px", fontWeight: 700 }}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

// ─── Sidebar Filter ───────────────────────────────────────────────────────────
// Only owns collapse state + the 5 action callbacks. All filter data/handlers
// (skills, job titles, location, radius, searchType, workAuth) live in
// SearchFilterContext and are read directly by AdvancedOptionsInline —
// nothing is drilled through here anymore.

const SidebarFilters = ({ collapsed, onToggleCollapse, onNewSearch, onClear, onUpdate, onFilterChange }: { collapsed: boolean; onToggleCollapse: () => void; onNewSearch: () => void; onClear: () => void; onUpdate: () => void; onFilterChange: (filters: any) => void }) => {
  const { resetKey } = useSearchFilters();

  return (
    <div style={{ width: collapsed ? "0px" : "310px", minWidth: collapsed ? "0px" : "310px", overflow: "hidden", transition: "width 0.25s ease, min-width 0.25s ease", borderRight: collapsed ? "none" : "1px solid #e5e7eb", backgroundColor: "#fff", flexShrink: 0, display: "flex", flexDirection: "column", height: "100vh" }}>
      {!collapsed && (
        <>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb", flexShrink: 0 }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Filters</span>
            <Button icon="pi pi-chevron-left" className="p-button-text p-button-sm p-0" style={{ color: "#6b7280" }} onClick={onToggleCollapse} />
          </div>

          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            <AdvancedOptionsInline key={resetKey} onFilterChange={onFilterChange} />
          </div>

          {/* Bottom buttons */}
          <div style={{ borderTop: "1px solid #e5e7eb", padding: "12px 16px", display: "flex", gap: "8px", flexShrink: 0 }}>
            <Button label="NEW SEARCH" className="p-button-outlined p-button-sm" style={{ flex: 1, fontSize: "12px", color: "#6b7280", borderColor: "#d1d5db" }} onClick={onNewSearch} />
            <Button label="CLEAR ALL" className="p-button-outlined p-button-sm" style={{ flex: 1, fontSize: "12px", color: "#6b7280", borderColor: "#d1d5db" }} onClick={onClear} />
            <Button label="UPDATE" className="p-button-sm" style={{ flex: 1, fontSize: "12px", backgroundColor: "#374151", border: "none" }} onClick={onUpdate} />
          </div>
        </>
      )}
    </div>
  );
};

// ─── Candidate Card ───────────────────────────────────────────────────────────
// Wrapped in React.memo — safe now because CandidateResults passes stable
// (useCallback'd) handlers, so a card only re-renders when its own props change.

const CandidateCard = memo(function CandidateCard({ candidate, onSelect, onNameClick, showSkills, onToggleSkills, searchQuery }: { candidate: Candidate; onSelect: (id: number) => void; onNameClick: (c: Candidate) => void; showSkills: boolean; onToggleSkills: (id: number) => void; searchQuery: string }) {
  const scoreColor = candidate.matchScore >= 9 ? "#16a34a" : candidate.matchScore >= 7 ? "#ca8a04" : "#dc2626";

  const initials = candidate.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div style={{ background: "#fff", border: "1px solid #e9e3f5", borderRadius: "12px", marginBottom: "12px", overflow: "hidden" }}>
      {/* ── HEADER ROW ────────────────────────────────────────────────────── */}
      <div style={{ background: "linear-gradient(135deg, #f5f0ff 0%, #faf8ff 100%)", padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px", borderBottom: "1px solid #ede8f8" }}>
        <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "linear-gradient(135deg, #C88BFF, #9333EA)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: 700, color: "#fff", flexShrink: 0, letterSpacing: "0.03em" }}>{initials}</div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex align-items-center gap-2 flex-wrap" style={{ marginBottom: "3px" }}>
            <button onClick={() => onNameClick(candidate)} style={{ background: "none", border: "none", cursor: "pointer", color: "#111827", fontSize: "15px", fontWeight: 700, padding: 0, lineHeight: 1.2 }}>
              {highlightText(candidate.name, searchQuery)}
            </button>

            {candidate.location && <span style={{ fontSize: "11px", color: "#9333EA", background: "rgba(147, 51, 234, 0.1)", border: "1px solid #ddd6fe", borderRadius: "20px", padding: "2px 8px", fontWeight: 500 }}>{candidate.location}</span>}
          </div>

          <div style={{ fontSize: "13px", color: "#4b5563" }}>
            {highlightText(candidate.currentTitle, searchQuery)}
            {candidate.company && candidate.company !== "—" && (
              <>
                {"  "}
                <span style={{ color: "#111827", fontWeight: 600 }}>{highlightText(candidate.company, searchQuery)}</span>
              </>
            )}
          </div>
        </div>

        <div style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: "4px", border: `2px solid ${scoreColor}`, borderRadius: "20px", padding: "4px 12px", color: scoreColor, fontWeight: 700, fontSize: "13px", background: "#DCF8E2", whiteSpace: "nowrap" }}>
          <span style={{ fontSize: "17px", fontWeight: 800 }}>{candidate.matchScore.toFixed(1)}</span>
          <span style={{ fontSize: "12px", fontWeight: 600, opacity: 0.85 }}>Match</span>
        </div>
      </div>

      {/* ── BODY ──────────────────────────────────────────────────────────── */}
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: "11px", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px" }}>Top Skill</div>

        <div className="flex flex-wrap gap-2" style={{ marginBottom: "14px" }}>
          {candidate.topSkills.map((skill, i) => (
            <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: "0", background: "#fff", border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden", fontSize: "12px" }}>
              <span style={{ padding: "4px 10px", fontWeight: 700, color: "#111827", borderRight: "1px solid #d1d5db" }}>• {highlightText(skill.name, searchQuery)}</span>
              <span style={{ padding: "4px 10px", color: "#6b7280", fontWeight: 400, background: "#f9fafb" }}>{skill.years}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2" style={{ background: "#f5f0ff", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px" }}>
          {[
            candidate.experience && candidate.experience !== "—" && { icon: "pi pi-briefcase", label: "Experience", value: candidate.experience, valueColor: "#111827" },
            candidate.highestEducation && candidate.highestEducation !== "—" && { icon: "pi pi-book", label: "Education", value: candidate.highestEducation, valueColor: "#111827" },
            candidate.relocation && { icon: "pi pi-map-marker", label: "Re-location", value: candidate.relocation, valueColor: candidate.relocation === "Will Relocate" ? "#16a34a" : "#dc2626" },
            candidate.authorization && candidate.authorization !== "—" && { icon: "pi pi-verified", label: "Auth", value: candidate.authorization, valueColor: "#111827" },
            candidate.resumeUpdated && candidate.resumeUpdated !== "—" && { icon: "pi pi-user-edit", label: "Update", value: candidate.resumeUpdated, valueColor: "#111827" },
            candidate.desiredSalary && candidate.desiredSalary !== "—" && { icon: "pi pi-dollar", label: "Salary", value: candidate.desiredSalary, valueColor: "#111827" },
          ]
            .filter(Boolean)
            .map((item: any, i) => (
              <div key={i} className="flex align-items-center gap-1" style={{ background: "#ede9fe", borderRadius: "6px", padding: "5px 10px", fontSize: "12px", whiteSpace: "nowrap" }}>
                <i className={item.icon} style={{ fontSize: "11px", color: "#7c3aed", marginRight: "4px" }} />
                <span style={{ color: "#999999", fontWeight: 600, marginRight: "4px" }}>{item.label}</span>
                <span style={{ color: item.valueColor, fontWeight: 700 }}>{item.value}</span>
              </div>
            ))}
        </div>

        <Button label={showSkills ? "Hide Skill" : "Show all skills"} icon={showSkills ? "pi pi-chevron-up" : "pi pi-chevron-down"} iconPos="right" className="p-button-text p-button-sm p-0" style={{ color: "#374151", fontSize: "12px", fontWeight: 600, gap: "4px" }} onClick={() => onToggleSkills(candidate.id)} />

        {showSkills && (
          <div style={{ marginTop: "10px" }}>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px" }}>All Skill</div>
            <div className="flex flex-wrap gap-2">
              {candidate.topSkills.map((skill, i) => (
                <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: "0", background: "#fff", border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden", fontSize: "12px" }}>
                  <span style={{ padding: "4px 10px", fontWeight: 700, color: "#111827", borderRight: "1px solid #d1d5db" }}>• {highlightText(skill.name, searchQuery)}</span>
                  <span style={{ padding: "4px 10px", color: "#6b7280", background: "#f9fafb" }}>{skill.years}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Skeleton ───────────────────────────────────────────────────────────────

const CandidateCardSkeleton = () => (
  <div className="card" style={{ marginBottom: "12px" }}>
    <div className="border-round border-1 surface-border p-4 surface-card">
      <div className="flex mb-3">
        <Skeleton shape="circle" size="4rem" className="mr-2" />
        <div>
          <Skeleton width="10rem" className="mb-2" />
          <Skeleton width="5rem" className="mb-2" />
          <Skeleton height=".5rem" />
        </div>
      </div>
      <Skeleton width="100%" height="150px" />
      <div className="flex justify-content-between mt-3">
        <Skeleton width="4rem" height="2rem" />
        <Skeleton width="4rem" height="2rem" />
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
// Only owns list/pagination/loading/usage props now. Filter state (workAuth,
// limitRelocate, etc.) is read from SearchFilterContext instead of being
// passed in as `filters`/`setFilters` props.

const CandidateResults = ({ results = [], totalResults = 0, searchQuery = "", onNewSearch, onCandidateClick, onUpdateFilters, first, setFirst, onPage, isLoading = false, useageData = { quota: 0, viewsRemaining: 0 }, totalReturned }: CandidateResultsProps) => {
  const { clearAll } = useSearchFilters();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [expandedSkills, setExpandedSkills] = useState<number[]>([]);
  const [filters, setFilters] = useState<any>({});
  const toast = useRef<Toast>(null);
  const rowsPerPage = 25;
  const filtersRef = useRef<any>({});

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  }, []);

  const toggleSkills = useCallback((id: number) => {
    setExpandedSkills((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  }, []);

  const handleClear = useCallback(() => {
    filtersRef.current = {};
    setFilters({});
    clearAll();
  }, [clearAll]);

  const handleFilterChange = useCallback((newFilters: any) => {
    filtersRef.current = newFilters;
    setFilters(newFilters);
  }, []);

  const handleUpdate = useCallback(() => {
    const f = filtersRef.current;
    if (f?.resumeUpdatedMinAge !== undefined && f?.resumeUpdatedMinAge !== null && (f?.resumeUpdatedMaxAge === undefined || f?.resumeUpdatedMaxAge === null || f?.resumeUpdatedMaxAge?.trim() === "")) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "For Resume Updated Minimum Age, Resume Updated Max Age is required in Filter", life: 3000 });
      return;
    }
    onUpdateFilters(f);
  }, [onUpdateFilters]);

  const filteredResults = useMemo(() => {
    let filtered = results;
    if (filters.workAuth && filters.workAuth !== "all") {
      filtered = filtered.filter((c) => c.authorization?.toLowerCase().includes(filters.workAuth.toLowerCase()));
    }
    if (filters.limitRelocate) {
      filtered = filtered.filter((c) => c.relocation === "Will Relocate");
    }
    return filtered;
  }, [results, filters.workAuth, filters.limitRelocate]);

  const totalPages = Math.ceil(totalResults / rowsPerPage);
  const groupSize = 5;

  const currentGroup = Math.floor((first - 1) / groupSize);
  const startPage = currentGroup * groupSize + 1;
  const endPage = Math.min(startPage + groupSize - 1, totalPages);

  function getPaginationRange(totalRecords: number, pageSize: number, currentPage: number) {
    const start = (currentPage - 1) * pageSize + 1;
    let end = currentPage * pageSize;
    if (end > totalRecords) {
      end = totalRecords;
    }
    return `${start} - ${end}`;
  }

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", display: "flex", backgroundColor: "#f3f4f6" }}>
      {/* ── Sidebar ── */}
      <SidebarFilters collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(true)} onNewSearch={onNewSearch} onClear={handleClear} onUpdate={handleUpdate} onFilterChange={handleFilterChange} />

      {/* Expand button when collapsed */}
      {sidebarCollapsed && (
        <div style={{ width: "32px", display: "flex", alignItems: "flex-start", paddingTop: "12px", borderRight: "1px solid #e5e7eb", backgroundColor: "#fff", flexShrink: 0 }}>
          <Button icon="pi pi-chevron-right" className="p-button-text p-button-sm p-0" style={{ color: "#6b7280", width: "32px" }} onClick={() => setSidebarCollapsed(false)} tooltip="Expand filters" tooltipOptions={{ position: "right" }} />
        </div>
      )}

      {/* ── Main Content ── */}
      <div style={{ flex: 1, overflow: "auto", padding: "0" }}>
        {/* Top bar */}
        <div style={{ backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb", padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", position: "sticky", top: 0, zIndex: 10 }}>
          {!isLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <span style={{ fontSize: "15px", fontWeight: 200, color: "#111827" }}>{`(${getPaginationRange(totalResults, 20, first)})`}</span>
              <span style={{ fontSize: "22px", fontWeight: 700, color: "#111827" }}>of</span>
              <span style={{ fontSize: "22px", fontWeight: 700, color: "#111827" }}>{totalResults}</span>
              <span style={{ fontSize: "14px", color: "#6b7280" }}>Results</span>
              <span style={{ fontSize: "15px", fontWeight: 200, color: "#111827" }}>{`Showing ${totalReturned} Candidates`}</span>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Tag value={`Views : ${useageData.viewsRemaining}/${useageData.quota}`} />
          </div>
        </div>

        {/* Toolbar row */}
        {!isLoading && (
          <div style={{ backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb", padding: "10px 24px", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            <Button
              icon="pi pi-angle-left"
              className="p-button-text p-button-sm"
              style={{ color: "#374151" }}
              onClick={() => {
                if (first - 1 > 0) {
                  setFirst(first - 1);
                  onPage(first - 1);
                }
              }}
            />
            {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
              const pageNum = startPage + i;

              return (
                <Button
                  key={pageNum}
                  label={String(pageNum)}
                  className="p-button-sm"
                  style={{ minWidth: "32px", padding: "4px", backgroundColor: first === pageNum ? "#9333ea" : "transparent", color: first === pageNum ? "#fff" : "#374151", border: first === pageNum ? "none" : "1px solid #d1d5db", fontSize: "13px" }}
                  onClick={() => {
                    setFirst(pageNum);
                    onPage(pageNum);
                  }}
                />
              );
            })}
            <Button
              icon="pi pi-angle-right"
              className="p-button-text p-button-sm"
              style={{ color: "#374151" }}
              onClick={() => {
                if (first + 1 <= Math.ceil(totalResults / rowsPerPage)) {
                  setFirst(first + 1);
                  onPage(first + 1);
                }
              }}
            />
            <Button
              icon="pi pi-angle-double-right"
              className="p-button-text p-button-sm"
              style={{ color: "#374151" }}
              onClick={() => {
                setFirst(Math.ceil(totalResults / rowsPerPage));
                onPage(Math.ceil(totalResults / rowsPerPage));
              }}
            />
          </div>
        )}

        {/* Candidate List */}
        <div style={{ padding: "16px 24px" }}>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => <CandidateCardSkeleton key={i} />)
          ) : results.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280", fontSize: "15px" }}>No candidates found.</div>
          ) : (
            filteredResults.map((candidate) => <CandidateCard key={candidate.id} candidate={candidate} onSelect={toggleSelect} onNameClick={onCandidateClick} showSkills={expandedSkills.includes(candidate.id)} onToggleSkills={toggleSkills} searchQuery={searchQuery} />)
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateResults;
export type { Candidate, CandidateResultsProps };
